import React from 'react';
import {
  Box,
  Button,
  Typography,
  FormHelperText,
  ThemeProvider,
  Card,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Formik, FormikHelpers } from 'formik';
import { useSettingsCreate } from '../../Query/SettingsQuery';
import * as Yup from 'yup';
import { uploadImage } from '../../HelperFunctions/s3Service';
import { toast } from 'react-hot-toast';
import { theme } from '../../../theme';
import { styled } from '@mui/material/styles';
import ImageIcon from '@mui/icons-material/Image';
import { DesignSettings } from '../../../client-sdk';

interface ImageField {
  file: File | null;
  url: string;
}

interface FormValues {
  logo: ImageField | null;
  favicon: ImageField | null;
  banner: ImageField | null; // Splash screen
  mobileLogo: ImageField | null; // Hero image
}

interface DesignSettingsProps {
  onClose?: () => void;
  designSettings?: {
    id?: string;
    logo?: string;
    favicon?: string;
    banner?: string;
    mobileLogo?: string;
  };
  appDesign: DesignSettings[];
  addDesignSettings?: (
    payload: {
      id: string;
      logo: string;
      favicon: string;
      banner: string;
      mobileLogo: string;
    },
    options?: { onSuccess?: () => void; onError?: (err: any) => void }
  ) => Promise<void>;
  isPending?: boolean;
}

const createImageValidation = (
  width: number,
  height: number,
  mime: 'image/png' | 'image/jpeg' | 'any',
  label: string
): Yup.Schema<ImageField | null> =>
  Yup.mixed<ImageField>()
    .required(`${label} is required`)
    .test(
      'fileFormat',
      `${label} must be a ${
        mime === 'any' ? 'valid image' : mime.split('/')[1].toUpperCase()
      } file`,
      (value) => !value?.file || mime === 'any' || value.file.type === mime
    )
    .test(
      'dimensions',
      `${label} must be exactly ${width}×${height}px`,
      (value) =>
        new Promise((resolve) => {
          if (!value?.file) return resolve(true); // editing existing URL: skip
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;
            img.onload = () =>
              resolve(img.width === width && img.height === height);
            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(value.file);
        })
    );

const validationSchema = Yup.object({
  logo: createImageValidation(320, 108, 'image/png', 'Logo'),
  favicon: createImageValidation(512, 512, 'image/png', 'Favicon'),
  banner: createImageValidation(480, 853, 'image/jpeg', 'Splash Screen'),
  mobileLogo: createImageValidation(592, 640, 'any', 'Hero Image'),
});

const CardContainer = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  padding: theme.spacing(3),
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

const DropzoneStyled = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#F8FAFC',
  cursor: 'pointer',
  minHeight: 180,
  minWidth: 180,
  transition: 'all 0.2s',
  '&:hover': {
    borderColor: theme.palette.primary.dark,
    backgroundColor: '#F1F5F9',
  },
}));

const PreviewBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 160,
  minWidth: 160,
  background: '#F8FAFC',
  borderRadius: 12,
  border: '1px solid #E5E7EB',
}));

const RecommendedText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  marginTop: theme.spacing(1),
}));

const FaviconPreview = ({ url }: { url: string }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: 50,
        height: 50,
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
      }}
    >
      <img src={url} alt="Favicon preview" style={{ width: 40, height: 40 }} />
    </Box>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
      }}
    >
      <img
        src={url}
        alt="Favicon tiny preview"
        style={{ width: 20, height: 20 }}
      />
    </Box>
  </Box>
);

const renderUploadCard = (
  field: keyof FormValues,
  label: string,
  recommended: string,
  formik: any,
  isFavicon = false
) => {
  const value: ImageField | null = formik.values[field];
  const hasError = formik.touched[field] && Boolean(formik.errors[field]);

  return (
    <CardContainer>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center',
          flex: 1,
        }}
      >
        {/* Dropzone */}
        <label htmlFor={field} style={{ width: '60%' }}>
          <input
            id={field}
            type="file"
            accept="image/*"
            hidden
            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0] ?? null;
              formik.setFieldTouched(field, true);
              if (!file) {
                formik.setFieldValue(field, null);
                return;
              }
              try {
                const { url } = await uploadImage(file);
                formik.setFieldValue(field, { file, url });
              } catch {
                formik.setFieldError(field, 'Upload failed. Please try again.');
              }
            }}
          />
          <DropzoneStyled>
            <ImageIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" align="center">
              Drag & drop image here
            </Typography>
            <Button
              variant="outlined"
              component="span"
              sx={{ mt: 1, fontWeight: 500 }}
              size="small"
            >
              Browse...
            </Button>
          </DropzoneStyled>
        </label>
        {/* Preview */}
        <PreviewBox>
          {value?.url ? (
            isFavicon ? (
              <FaviconPreview url={value.url} />
            ) : (
              <img
                src={value.url}
                alt={`${label} preview`}
                style={{ maxWidth: 120, maxHeight: 80, objectFit: 'contain' }}
              />
            )
          ) : (
            <Box sx={{ color: 'text.disabled', fontSize: 32, fontWeight: 700 }}>
              —
            </Box>
          )}
        </PreviewBox>
      </Box>
      <Box mt={2} display="flex" alignItems="center" gap={2}>
        {value && (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              formik.setFieldValue(field, null);
              (document.getElementById(field) as HTMLInputElement).value = '';
            }}
          >
            Clear
          </Button>
        )}
        <Typography variant="body2" color="text.secondary">
          {value?.file ? value.file.name : 'No file chosen'}
        </Typography>
      </Box>
      <RecommendedText>{recommended}</RecommendedText>
      {hasError && (
        <FormHelperText error>{formik.errors[field]}</FormHelperText>
      )}
    </CardContainer>
  );
};

const DesignSetting: React.FC<DesignSettingsProps> = ({
  onClose,
  designSettings,
  appDesign,
  // isPending = false,
}) => {
  const navigate = useNavigate();
  const { mutate: createSettings, isPending: createLoading } =
    useSettingsCreate();
  const initialValues: FormValues = {
    logo: appDesign?.[0]?.logoUrl
      ? { file: null, url: appDesign[0].logoUrl }
      : designSettings?.logo
        ? { file: null, url: designSettings.logo }
        : null,
    favicon: appDesign?.[0]?.faviconUrl
      ? { file: null, url: appDesign[0].faviconUrl }
      : designSettings?.favicon
        ? { file: null, url: designSettings.favicon }
        : null,
    banner: appDesign?.[0]?.splashScreenUrl
      ? { file: null, url: appDesign[0].splashScreenUrl }
      : designSettings?.banner
        ? { file: null, url: designSettings.banner }
        : null,
    mobileLogo: appDesign?.[0]?.heroImageUrl
      ? { file: null, url: appDesign[0].heroImageUrl }
      : designSettings?.mobileLogo
        ? { file: null, url: designSettings.mobileLogo }
        : null,
  };

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    const payload = {
      appDesignData: {
        logoUrl: values.logo?.url ?? '',
        faviconUrl: values.favicon?.url ?? '',
        splashScreenUrl: values.banner?.url ?? '',
        heroImageUrl: values.mobileLogo?.url ?? '',
        appColor: '#000000',
      },
    };
    createSettings(payload, {
      onSuccess: () => {
        toast.success('Design settings saved successfully');
        helpers.resetForm();
        ['logo', 'favicon', 'banner', 'mobileLogo'].forEach((field) => {
          const input = document.getElementById(field) as HTMLInputElement;
          if (input) {
            input.value = '';
          }
        });
      },
      onError: (error) => {
        console.error(error);
        toast.error('Failed to save design settings');
      },
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Design Settings
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} noValidate>
            <Box sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  justifyContent: 'center',
                  alignItems: 'stretch',
                  maxWidth: 900,
                  margin: '0 auto',
                }}
              >
                <Box sx={{ flex: '1 1 45%', minWidth: 260, maxWidth: 400 }}>
                  {renderUploadCard(
                    'logo',
                    'Logo',
                    'Recommended size: 320x108px',
                    formik
                  )}
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: 260, maxWidth: 400 }}>
                  {renderUploadCard(
                    'favicon',
                    'Favicon',
                    'Recommended size: 512x512px',
                    formik,
                    true
                  )}
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: 260, maxWidth: 400 }}>
                  {renderUploadCard(
                    'banner',
                    'Splash Screen',
                    'Recommended size: 480x853px',
                    formik
                  )}
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: 260, maxWidth: 400 }}>
                  {renderUploadCard(
                    'mobileLogo',
                    'Hero Image',
                    'Recommended size: 592x640px',
                    formik
                  )}
                </Box>
              </Box>
              <Box display="flex" gap={2} justifyContent="center" mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createLoading || formik.isSubmitting}
                >
                  {createLoading || formik.isSubmitting ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    if (onClose) onClose();
                    else navigate(-1);
                    if (typeof formik !== 'undefined' && formik.resetForm) {
                      formik.resetForm();
                      ['logo', 'favicon', 'banner', 'mobileLogo'].forEach(
                        (field) => {
                          const input = document.getElementById(
                            field
                          ) as HTMLInputElement;
                          if (input) input.value = '';
                        }
                      );
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </ThemeProvider>
  );
};

export default DesignSetting;